import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["globalSubmitPanel", "localSaveButton", "nextButton", "disableValidations"];

  connect() {
    this.element.dataset.action +=
      ` form-change-monitor:formDirtied->global-save-button#showGlobalSubmitPanel
        form-change-monitor:connected@window->global-save-button#attachSubmitButtonListeners
      `;

    this.attachSubmitButtonListeners();

    addEventListener("turbo:before-stream-render", (event) => {
      const fallbackToDefaultActions = event.detail.render;

      event.detail.render = (streamElement) => {
        if (
          ["update", "replace"].includes(streamElement.action) &&
          (
            /section_status_.*/.test(streamElement.target) ||
            /nav_with_status_.*/.test(streamElement.target)
          )
        ) {
          this.attachSubmitButtonListeners();
        }
        fallbackToDefaultActions(streamElement);
      };
    });

    setTimeout(() => {
      this.element.dataset.js = 'ready' // Bug fix: notify UI that the controller is ready
    }, 0)
  }

  attachSubmitButtonListeners() {
    this.localSaveButtonTargets.forEach((submitButton) => {
      // console.log("Register local button: ", submitButton);
      // let the local save button know that we took it over:
      submitButton.dataset.overridden = "true";
      submitButton.classList.add("hidden");
    });
  }

  showGlobalSubmitPanel(event) {
    if (event.detail.isModified) {
      this.globalSubmitPanelTarget.classList.remove("hidden");
      if (this.hasNextButtonTarget) {
        this.nextButtonTarget.classList.add("hidden");
      }
    } else {
      // Check if any form is still dirty
      const anyDirty = Array.from(
        document.querySelectorAll(
          '[data-form-change-monitor-target="submitButton"]'
        )
      ).some((button) => button.dataset.shouldSave === "true");

      if (!anyDirty) {
        this.globalSubmitPanelTarget.classList.add("hidden");
        if (this.hasNextButtonTarget) {
          this.nextButtonTarget.classList.remove("hidden");
        }
      }
    }
  }

  submitAllLocalForms() {
    const chains = []
    const chainedButtons = {}
    const submissions = []

    this.localSaveButtonTargets.forEach((submitButton) => {
      const shouldSave = submitButton.dataset.shouldSave === "true";
      const chain = submitButton.dataset.chain
      if (chain) {
        if (!chainedButtons[chain]) chainedButtons[chain] = [];
        if (shouldSave && !chains.includes(chain)) chains.push(chain);
        chainedButtons[chain].push(submitButton);
        return
      }

      if (shouldSave) {
        this.checkDisableValidations(submitButton);
        submissions.push(this.clickAndAwaitSubmit(submitButton));
      }
    });

    submissions.push(this.processChainedButtons(chains, chainedButtons))
    this.globalSubmitPanelTarget.classList.add("hidden");

    return Promise.all(submissions)
  }

  // These are buttons that have to wait for one another to prevent conflicting UI updates
  processChainedButtons(chains, chainedButtons) {
    return Promise.all(chains.map((chain) => this.processChain(chainedButtons[chain])))
  }

  processChain(buttons) {
    return new Promise((resolve) => {
      const clickAndTrack = (button) => {
        this.checkDisableValidations(button);
        const done = this.clickAndAwaitSubmit(button)
        if (buttons.length === 0) done.then(resolve)
      }

      const handler = () => {
        const button = buttons.shift()
        if (button) clickAndTrack(button)
        if (buttons.length === 0) {
          window.removeEventListener('form-change-monitor:connected', handler)
        }
      }
      window.addEventListener('form-change-monitor:connected', handler)

      const firstButton = buttons.shift()
      if (firstButton) {
        clickAndTrack(firstButton)
      } else {
        window.removeEventListener('form-change-monitor:connected', handler)
        resolve()
      }
    })
  }

  // Clicks a submit button and resolves once ITS form's submission has finished
  // (turbo:submit-end), not merely once it's been kicked off.
  clickAndAwaitSubmit(submitButton) {
    const done = submitButton.form
      ? this.waitForEvent(submitButton.form, 'turbo:submit-end')
      : Promise.resolve()

    submitButton.click()
    return done
  }

  waitForEvent(target, eventName) {
    return new Promise((resolve) => {
      target.addEventListener(eventName, resolve, { once: true })
    })
  }

  checkDisableValidations(submitButton) {
    if (!this.hasDisableValidationsTarget) return;
    if (!this.disableValidationsTarget.checked) return;

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "disable_validations"
    input.value = "1"
    submitButton.form.appendChild(input)
  }
}
